import { useMemo, useState } from "react";
import { Search as SearchIcon } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { listPublishedReports, type ReportRow } from "@/lib/reports.functions";
import { hreflangLinks, absUrl } from "@/lib/seo";
import { reportSlugMap } from "@/lib/report-slug";
import { pageTitle } from "@/lib/brand";
import { PageIntro, PageSection, Chip, LinkRows, MoreLink, type RowItem } from "@/components/site/page";

type Lang = "tr" | "en";

const CATEGORY_LABELS: Record<string, { tr: string; en: string }> = {
  ai: { tr: "Yapay Zekâ", en: "AI" },
  climate: { tr: "İklim", en: "Climate" },
  energy: { tr: "Enerji", en: "Energy" },
  sustainability: { tr: "Sürdürülebilirlik", en: "Sustainability" },
  biodiversity: { tr: "Biyoçeşitlilik", en: "Biodiversity" },
  economy: { tr: "Ekonomi", en: "Economy" },
  "turkiye-politika": { tr: "Türkiye Politika", en: "Türkiye Policy" },

  society: { tr: "Toplum", en: "Society" },
  other: { tr: "Diğer", en: "Other" },
};

const TYPE_LABELS: Record<string, { tr: string; en: string }> = {
  report: { tr: "Rapor", en: "Report" },
  index: { tr: "Endeks", en: "Index" },
  dataset: { tr: "Veri Seti", en: "Dataset" },
  framework: { tr: "Çerçeve", en: "Framework" },
};

function catLabel(c: string, lang: Lang) {
  return CATEGORY_LABELS[c]?.[lang] ?? c;
}
function typeLabel(t: string, lang: Lang) {
  return TYPE_LABELS[t]?.[lang] ?? t;
}

export function reportsHead(lang: Lang, opts?: { path?: string; trPath?: string; enPath?: string }) {
  const path = opts?.path ?? (lang === "tr" ? "/raporlar" : "/research");
  // English report pages are canonical on the English brand domain.
  const url = absUrl(lang === "en" ? (opts?.enPath ?? path) : path);
  const title =
    lang === "tr"
      ? pageTitle("Rapor Kütüphanesi", "tr")
      : pageTitle("Source Notes — Global Research", "en");
  const description =
    lang === "tr"
      ? "İklim, yapay zekâ, sürdürülebilirlik, ekonomi ve toplum üzerine saygın küresel rapor, endeks ve araştırmaların seçilmiş referans kütüphanesi."
      : "Source notes on major global reports and indices in AI, climate, sustainability, economy, and society — the reading behind the essays.";
  const trPath = opts?.trPath ?? "/raporlar";
  const enPath = opts?.enPath ?? (lang === "tr" ? "/research" : path);
  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
      { property: "og:locale", content: lang === "tr" ? "tr_TR" : "en_US" },
      { property: "og:locale:alternate", content: lang === "tr" ? "en_US" : "tr_TR" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [
      { rel: "canonical", href: url },
      ...hreflangLinks(trPath, enPath),
    ],
  };
}


export function ReportsLibrary({ lang, initialData }: { lang: Lang; initialData?: ReportRow[] }) {
  const fetchFn = useServerFn(listPublishedReports);
  const { data, isLoading } = useQuery({
    queryKey: ["public-reports"],
    queryFn: () => fetchFn(),
    initialData,
  });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<string>("all");

  const reports: ReportRow[] = data ?? [];
  const categories = useMemo(() => {
    const set = new Set<string>();
    reports.forEach((r) => set.add(r.category));
    return Array.from(set).sort();
  }, [reports]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return reports.filter((r) => {
      if (cat !== "all" && r.category !== cat) return false;
      if (!needle) return true;
      const hay = [
        r.title,
        r.organization,
        r.summary_tr,
        r.summary_en,
        ...(r.topics ?? []),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [reports, q, cat]);

  // TR side gives every report its own Turkish summary page; the slug map is
  // derived from the same list so library links and the route always agree.
  const slugs = useMemo(() => reportSlugMap(reports), [reports]);

  const items: RowItem[] = filtered.map((r) => {
    const summary = lang === "tr" ? r.summary_tr || r.summary_en : r.summary_en || r.summary_tr;
    const slug = slugs.get(r.id) ?? r.id;
    const detailProps = lang === "tr" ? { to: "/raporlar/$slug" as const, params: { slug } } : { to: "/research/$slug" as const, params: { slug } };
    return {
      key: r.id,
      ...detailProps,
      kicker: (
        <span className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-ink/70">
          <span>{catLabel(r.category, lang)}</span>
          <span className="rounded-full border border-ink/15 px-2 py-0.5 normal-case tracking-normal">{typeLabel(r.type, lang)}</span>
          {r.year && <span>{r.year}</span>}
        </span>
      ),
      title: r.title,
      excerpt: summary ?? undefined,
      meta: r.organization || undefined,
    };
  });

  return (
    <div>
      <PageIntro
        crumbs={[{ label: lang === "tr" ? "Rapor Kütüphanesi" : "Source Notes" }]}
        kicker={lang === "tr" ? "Referans Kütüphanesi" : "Reference Library"}
        title={lang === "tr" ? "Rapor Kütüphanesi" : "Source Notes"}
        lede={
          lang === "tr"
            ? "Yazılarda yaslanılan, saygın kurumlarca yayımlanmış küresel rapor, endeks ve araştırmaların seçilmiş bir derlemesi. Düzenli olarak güncellenir."
            : "Reading notes on the global reports, indices, and research that inform the essays here — updated regularly."
        }
      >
        <label htmlFor="reports-search" className="sr-only">
          {lang === "tr" ? "Raporlarda ara" : "Search reports"}
        </label>
        <div className="relative mt-6 max-w-xl">
          <SearchIcon aria-hidden className="absolute left-4 top-1/2 size-4 -translate-y-1/2 text-ink/70" />
          <input
            id="reports-search"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={lang === "tr" ? "Başlık, kurum veya konuda ara…" : "Search title, organization or topic…"}
            className="min-h-11 w-full rounded-full border border-ink/20 bg-paper pl-11 pr-4 text-base focus:border-ink focus:outline-none"
          />
        </div>
      </PageIntro>

      <PageSection>
        <div className="flex flex-wrap gap-2">
          <Chip active={cat === "all"} onClick={() => setCat("all")}>
            {lang === "tr" ? "Tümü" : "All"}
          </Chip>
          {categories.map((c) => (
            <Chip key={c} active={cat === c} onClick={() => setCat(c)}>
              {catLabel(c, lang)}
            </Chip>
          ))}
        </div>

        <div className="mt-8">
          {isLoading ? (
            <p className="text-sm text-ink/60">{lang === "tr" ? "Yükleniyor…" : "Loading…"}</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-ink/60">{lang === "tr" ? "Sonuç bulunamadı." : "No results."}</p>
          ) : (
            <LinkRows items={items} />
          )}
        </div>

        <div className="mt-8">
          <MoreLink to="/">{lang === "tr" ? "Ana sayfa" : "Home"}</MoreLink>
        </div>
      </PageSection>
    </div>
  );
}
